import React, { ReactNode, useState } from "react";
import { clsx } from "clsx";
import { Empty } from "../Empty";
import { Input } from "../Input";
import classes from "./TableView.module.less";

export interface TableViewColumn {
    key: string;
    label: ReactNode;
    /**
     * CSS grid track for the column, e.g. "2fr" or "120px". Without one the
     * column is sized by its content (header and cells alike) and takes an
     * equal share of whatever space is left; when space runs short it
     * shrinks and its cells clip with an ellipsis.
     */
    width?: string;
    /** Cell content appears only while its row is hovered (or focused). */
    hoverOnly?: boolean;
}

export interface TableViewRow {
    key: string;
    /** Plain text the search bar matches against, typically the row's name. */
    searchText: string;
    /** Cell content per column key. */
    values: Record<string, ReactNode>;
}

export interface TableViewProps {
    columns: TableViewColumn[];
    rows: TableViewRow[];
    searchPlaceholder?: string;
    onRowClick?: (row: TableViewRow) => void;
    className?: string;
}

/**
 * A simple table: a search bar filtering rows on their searchText, a header
 * row naming the columns, and the matching rows.
 */
export const TableView: React.FC<TableViewProps> = ({
    columns,
    rows,
    searchPlaceholder = "Search",
    onRowClick,
    className,
}) => {
    const [query, setQuery] = useState("");

    const needle = query.trim().toLowerCase();
    const visibleRows = needle
        ? rows.filter((row) => row.searchText.toLowerCase().includes(needle))
        : rows;

    // The whole table is one grid (the header and the rows are
    // display: contents), so a content-sized track aligns across rows.
    const template = columns.map((column) => column.width ?? "auto").join(" ");

    return (
        <div className={clsx(classes["table-view"], className)}>
            <Input
                className={classes["table-view__search"]}
                placeholder={searchPlaceholder}
                allowClear
                value={query}
                onChange={(event) => setQuery(event.target.value)}
            />
            <div
                className={classes["table-view__grid"]}
                style={{ "--table-columns": template } as React.CSSProperties}
            >
                <div className={classes["table-view__header"]}>
                    {columns.map((column) => (
                        <span key={column.key} className={classes["table-view__header-cell"]}>
                            {column.label}
                        </span>
                    ))}
                </div>
                {visibleRows.map((row) => (
                    <div
                        key={row.key}
                        className={clsx(
                            classes["table-view__row"],
                            onRowClick && classes["table-view__row--clickable"]
                        )}
                        onClick={onRowClick && (() => onRowClick(row))}
                    >
                        {columns.map((column) => (
                            <div
                                key={column.key}
                                className={clsx(
                                    classes["table-view__cell"],
                                    column.hoverOnly && classes["table-view__cell--hover-only"]
                                )}
                            >
                                {row.values[column.key]}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            {visibleRows.length === 0 && (
                <Empty
                    className={classes["table-view__empty"]}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={rows.length === 0 ? "Nothing here yet." : "No matches."}
                />
            )}
        </div>
    );
};

TableView.displayName = "TableView";
